
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X, Save, ThumbsDown, PackageX, MapPinOff, AlertOctagon, HelpCircle } from 'lucide-react';
import { Order, Incident, IncidentType } from '../types';
import { SmartNumberInput } from './SmartNumberInput';
import { toast } from 'react-hot-toast';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  order: Order | null;
  onSaveIncident: (orderId: string, incident: Incident) => void;
}

export const IncidentModal: React.FC<Props> = ({ isOpen, onClose, order, onSaveIncident }) => {
  const [type, setType] = useState<IncidentType>('refusal');
  const [description, setDescription] = useState('');
  const [valueImpact, setValueImpact] = useState(0);

  if (!isOpen || !order) return null;

  const handleSave = () => {
    if (!description.trim()) {
      toast.error('Descreva o ocorrido.');
      return;
    }

    const newIncident: Incident = {
      id: Math.random().toString(36).substr(2, 6),
      type,
      description,
      valueImpact: -Math.abs(valueImpact),
      timestamp: Date.now(),
      resolved: false,
      severity: valueImpact > 50 ? 'high' : valueImpact > 20 ? 'medium' : 'low', // v56.11: Auto-calculate severity
      reportedBy: 'dispatcher' // v56.11: Default reporter
    };

    onSaveIncident(order.id, newIncident);
    onClose();
    setType('refusal');
    setDescription('');
    setValueImpact(0);
  };

  const incidentTypes: { id: IncidentType, label: string, icon: any, color: string }[] = [
    { id: 'refusal', label: 'Recusa', icon: ThumbsDown, color: 'bg-rose-500/20 text-rose-400 border-rose-500/40' },
    { id: 'wrong_item', label: 'Item Errado', icon: PackageX, color: 'bg-orange-500/20 text-orange-400 border-orange-500/40' },
    { id: 'damage', label: 'Dano', icon: AlertOctagon, color: 'bg-amber-500/20 text-amber-400 border-amber-500/40' },
    { id: 'address_issue', label: 'Endereço', icon: MapPinOff, color: 'bg-blue-500/20 text-blue-400 border-blue-500/40' },
    { id: 'other', label: 'Outro', icon: HelpCircle, color: 'bg-white/10 text-slate-300 border-white/20' },
  ];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-sal-gunmetal/90 backdrop-blur-md"
        />

        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="relative bg-sal-gunmetal-light/80 w-full max-w-lg rounded-[2.5rem] shadow-[0_0_50px_rgba(244,63,94,0.15)] overflow-hidden border border-rose-500/20 backdrop-blur-2xl"
        >
          <div className="bg-rose-950/40 px-8 py-6 border-b border-rose-500/20 flex justify-between items-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-rose-500 shadow-[0_0_15px_#f43f5e]" />
            <div className="flex items-center gap-4 relative z-10">
              <div className="p-3 bg-rose-600 rounded-2xl text-white shadow-lg">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-black text-2xl text-white uppercase tracking-tighter">Relatório de Crise</h3>
                <p className="text-xs font-black text-rose-400 uppercase tracking-widest">Pedido #{order.id}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 bg-white/5 hover:bg-white/10 rounded-full transition-colors"><X className="w-5 h-5 text-slate-400" /></button>
          </div>

          <div className="p-8 space-y-6">
            <div className="grid grid-cols-5 gap-2">
              {incidentTypes.map(t => (
                <button
                  key={t.id}
                  onClick={() => setType(t.id)}
                  className={`flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border transition-all ${type === t.id ? `ring-2 ring-white/10 ${t.color}` : 'border-white/5 bg-white/5 opacity-40 hover:opacity-100 grayscale hover:grayscale-0'}`}
                >
                  <t.icon className="w-5 h-5" />
                  <span className="text-xs font-black uppercase tracking-widest text-center leading-tight">{t.label}</span>
                </button>
              ))}
            </div>

            <div className="bg-black/40 p-5 rounded-[1.5rem] border border-white/5">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <label className="text-xs font-black text-rose-400 uppercase tracking-widest block">Perda Financeira</label>
                  <span className="text-xs text-slate-500 font-bold uppercase">Impacto direto no caixa</span>
                </div>
                <div className="p-2 bg-rose-900/20 rounded-lg"><AlertOctagon className="w-4 h-4 text-rose-500" /></div>
              </div>
              <SmartNumberInput
                label=""
                value={valueImpact}
                onChange={setValueImpact}
                isCurrency
                step={1}
              />
            </div>

            <div>
              <label className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2 block ml-1">Observações Operacionais</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full bg-black/40 border border-white/5 rounded-2xl p-4 text-base text-slate-200 leading-relaxed font-medium focus:border-rose-500/40 outline-none resize-none h-24 shadow-inner"
                placeholder="Descreva a ocorrência em detalhes..."
              />
            </div>
          </div>

          <div className="p-6 bg-sal-gunmetal border-t border-white/5 flex justify-end gap-3">
            <button onClick={onClose} className="px-6 py-3 text-slate-500 font-black uppercase text-xs tracking-widest hover:bg-white/5 rounded-xl">Cancelar</button>
            <button
              onClick={handleSave}
              className="px-10 py-4 bg-rose-600 text-white font-black rounded-2xl hover:bg-rose-500 active:scale-95 transition-all shadow-xl shadow-rose-900/20 flex items-center gap-2 uppercase text-xs tracking-widest"
            >
              <Save className="w-4 h-4" /> Registrar Ocorrência
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
